import React, { ReactNode, useMemo } from 'react';
import ProfileLogo from '~/components/office/ProfileLogo';
import { cn, groupByFirstLetter } from '~/helpers/utils';

interface CompanyItem {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
}

type CompaniesAlphabeticalListProps<T extends CompanyItem> = {
  path: string;
  companies: T[];
  children?: (item: T) => ReactNode;
};

const CompaniesAlphabeticalListComponent: React.FC<
  CompaniesAlphabeticalListProps<any>
> = ({ companies, path, children }) => {
  const groupedCompanies = useMemo(
    () => groupByFirstLetter(companies),
    [companies]
  );

  return (
    <div className="space-y-4">
      {Object.keys(groupedCompanies)
        .sort()
        .map((letter) => (
          <div key={letter}>
            <h3 className="mb-2.5 font-display text-3xl font-bold text-stone-200">
              {letter}
            </h3>
            <div className="space-y-3">
              {groupedCompanies[letter].map((company) => (
                <a
                  href={`${path}/${company.slug}`}
                  key={company.id}
                  className={cn(
                    'group flex w-full gap-4 items-start rounded border border-stone-200 bg-white p-3 no-underline transition-shadow hover:shadow-md',
                    'md:p-5 md:items-center md:gap-6',
                  )}
                >
                  <ProfileLogo
                    src={company.logoUrl}
                    alt={`Logo van ${company.name}`}
                    spacingClass="p-1"
                    widthClass="w-12 md:w-24"
                  />
                  <div className="flex flex-col">
                    <h4 className={cn(
                      'font-display text-lg leading-snug font-bold text-blue-600 group-hover:text-blue-700',
                      'md:text-xl'
                    )}>
                      {company.name}
                    </h4>
                    {children?.(company)}
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

const CompaniesAlphabeticalList = <T extends CompanyItem>(
  props: CompaniesAlphabeticalListProps<T>
) => <CompaniesAlphabeticalListComponent {...props} />;

export default CompaniesAlphabeticalList;
